import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { debounce } from 'quasar';
import { onBeforeUnmount, Ref, ref, useSlots, watch, watchEffect } from 'vue';
import ShowMoreButton from 'components/ShowMoreButton.vue';
import useSetTimeout from 'composables/set-timeout';


export default /*#__PURE__*/_defineComponent({
  __name: 'ScrollArea',
  props: {
    contentClass: { default: '' },
    hideShowMore: { type: Boolean },
    isEmpty: { type: Boolean },
    maxItemsShown: { default: 5 }
  },
  setup(__props: any) {

const props = __props as { contentClass: string | object, hideShowMore?: boolean, isEmpty?: boolean, maxItemsShown: number };



const list: Ref<{ $el: HTMLDivElement } | undefined> = ref();
const listContent: Ref<HTMLDivElement | undefined> = ref();
const listScrollable: Ref<HTMLDivElement | undefined> = ref();
const items = <Ref<HTMLDivElement[]>>ref([]);
const maxHeight = ref(null as number | null);
const firstItemIndexInView = ref(0);
const showMore = ref(false);

const onScroll = debounce(() => {
  if (listContent.value && listScrollable.value) {
    const height = listContent.value.scrollHeight;
    const segmentSize = height / items.value.length;
    firstItemIndexInView.value = Math.floor(listScrollable.value.scrollTop / segmentSize);
    showMore.value = listScrollable.value.scrollTop + listScrollable.value.offsetHeight < height - 5;
  }
});

function scrollDown() {
  if (listContent.value) {
    let nextIndex = firstItemIndexInView.value + props.maxItemsShown;
    if (nextIndex > items.value.length - 1) {
      nextIndex = items.value.length - 1;
    }
    listScrollable.value?.scrollTo({ top: items.value[nextIndex].offsetTop, behavior: 'smooth' });
  }
}

watchEffect(() => {
  if (list.value?.$el && !listContent.value) {
    listContent.value = <HTMLDivElement>list.value.$el.querySelector('.q-scrollarea__content');
    listScrollable.value = <HTMLDivElement>list.value.$el.querySelector('.scroll');
    listScrollable.value.classList.add('contain-scroll');
    listScrollable.value.addEventListener('scroll', onScroll);
    void updateHeight();
  }
});

const wait = useSetTimeout();
async function updateHeight() {
  const CONTAINER_PADDING_HEIGHT = 16;

  await wait(0);
  if (listContent.value) {
    items.value = [...(<NodeListOf<HTMLDivElement>>listContent.value.querySelectorAll('.q-item'))];
    if (!items.value.length && !props.isEmpty) {
      console.error('ScrollArea error: No .q-item elements found!');
      console.info('Use "is-empty" attribute to tell ScrollArea not to expect content.');
      return;
    }

    // Use height of first items until max item display is reached
    maxHeight.value = [...new Array(props.maxItemsShown)]
      .map((_, index) => {
        const item = items.value[index];

        if (item) {
          const style = window.getComputedStyle(item);
          const marginHeight = parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
          return item.offsetHeight + marginHeight;
        }

        return 0;
      })
      .reduce((total, height) => total + height, 0);

    maxHeight.value += CONTAINER_PADDING_HEIGHT;

    showMore.value = listContent.value.scrollHeight > maxHeight.value;
  }
}

watch(
  () => ({ ...props }),
  (value, oldValue) => {
    if (value.maxItemsShown !== oldValue.maxItemsShown) {
      void updateHeight();
    }
  }
);

const slots = useSlots();
watch(
  () => (slots.default ? slots.default() : null),
  (value, oldValue) => {
    if (value !== oldValue) {
      void updateHeight();
    }
  }
);

onBeforeUnmount(() => {
  if (listScrollable.value) {
    listScrollable.value.removeEventListener('scroll', onScroll);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_resize_observer = _resolveComponent("q-resize-observer")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!__props.hideShowMore)
      ? (_openBlock(), _createBlock(ShowMoreButton, {
          key: 0,
          show: showMore.value,
          onClick: scrollDown
        }, null, 8, ["show"]))
      : _createCommentVNode("", true),
    _createVNode(_component_q_scroll_area, {
      ref_key: "list",
      ref: list,
      style: _normalizeStyle(`height: ${maxHeight.value}px`)
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: _normalizeClass(__props.contentClass)
        }, [
          _renderSlot(_ctx.$slots, "default"),
          _createVNode(_component_q_resize_observer, { onResize: updateHeight })
        ], 2)
      ]),
      _: 3
    }, 8, ["style"])
  ], 64))
}
}

})